import { isTimePermissionEnabled } from '../config/applicationFeatures';
import {
  BUSINESS_TIME_ZONE,
  formatDurationCompact,
  formatTime,
  parseCalendarDateInput,
  toCalendarDateString,
} from './dateTime';
import type { AttendanceReportRow } from './attendanceReport';
import {
  formatAttendanceReportDayDetails,
  formatPunchTimeWithSource,
  mapAttendanceReportRows,
} from './attendanceReport';
import type { WeeklyAttendanceDay } from '../types/weeklyAttendanceReport';
import {
  isMixedPunchActionSource,
  punchActionSourceLabel,
} from './attendanceEventSource';
import { shortHoursMinutesFromCoverage } from './workHoursCoverage';
import { formatStatusWithNotes } from './attendanceDayStatus';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function businessToday(now = new Date()): Date {
  const formatted = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return parseCalendarDateInput(formatted) ?? now;
}

export function startOfIsoWeek(date: Date): Date {
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
  const weekday = local.getDay();
  const offset = weekday === 0 ? -6 : 1 - weekday;
  local.setDate(local.getDate() + offset);
  return local;
}

export function getIsoWeekBounds(anchor = businessToday()): {
  weekStart: string;
  weekEnd: string;
} {
  const monday = startOfIsoWeek(anchor);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    weekStart: toCalendarDateString(monday),
    weekEnd: toCalendarDateString(sunday),
  };
}

export function shiftIsoWeek(
  weekStart: string,
  deltaWeeks: number,
): { weekStart: string; weekEnd: string } {
  const start = parseCalendarDateInput(weekStart) ?? businessToday();
  start.setDate(start.getDate() + deltaWeeks * 7);
  return getIsoWeekBounds(start);
}

export function formatWeekRangeLabel(weekStart: string, weekEnd: string): string {
  const start = parseCalendarDateInput(weekStart);
  const end = parseCalendarDateInput(weekEnd);
  if (!start || !end) return `${weekStart} - ${weekEnd}`;
  const startLabel = `${MONTHS[start.getMonth()]} ${start.getDate()}`;
  const sameYear = start.getFullYear() === end.getFullYear();
  const endLabel = sameYear
    ? `${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`
    : `${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
  return `${startLabel} - ${endLabel}`;
}

export function formatWeekOfLabel(weekStart: string, weekEnd: string): string {
  return `Week of: ${formatWeekRangeLabel(weekStart, weekEnd)}`;
}

export function formatWeeklyDateLabel(date: string): string {
  const parsed = parseCalendarDateInput(date);
  if (!parsed) return date;
  return `${WEEKDAYS[parsed.getDay()]} ${parsed.getDate()}`;
}

export function formatWeeklyTime(value: string | null | undefined): string {
  if (!value) return '—';
  const formatted = formatTime(value);
  return formatted === 'Not available' ? '—' : formatted;
}

export function formatWeeklyHours(minutes: number | null | undefined): string {
  return formatDurationCompact(minutes);
}

function weeklyPunchSource(day: WeeklyAttendanceDay, which: 'in' | 'out') {
  if (which === 'in') {
    return {
      source: day.checkInSource,
      originalSource: day.checkInOriginalSource,
      kioskName: day.checkInKioskName,
      kioskId: day.checkInKioskId,
      faceVerified: day.checkInFaceVerified,
      deviceIdentifier: day.checkInDeviceIdentifier,
    };
  }
  return {
    source: day.checkOutSource,
    originalSource: day.checkOutOriginalSource,
    kioskName: day.checkOutKioskName,
    kioskId: day.checkOutKioskId,
    faceVerified: day.checkOutFaceVerified,
    deviceIdentifier: day.checkOutDeviceIdentifier,
  };
}

export function weeklyCheckInDisplay(day: WeeklyAttendanceDay): string {
  return formatPunchTimeWithSource(
    day.checkIn ?? formatWeeklyTime(day.arrivalTime),
    day.checkInSourceLabel ?? punchActionSourceLabel(weeklyPunchSource(day, 'in')),
  );
}

export function weeklyCheckOutDisplay(day: WeeklyAttendanceDay): string {
  return formatPunchTimeWithSource(
    day.checkOut ?? formatWeeklyTime(day.exitTime),
    day.checkOutSourceLabel ?? punchActionSourceLabel(weeklyPunchSource(day, 'out')),
  );
}

export function weeklyHighlightCheckOut(day: WeeklyAttendanceDay): boolean {
  if (typeof day.highlightCheckOutSource === 'boolean') {
    return day.highlightCheckOutSource;
  }
  return isMixedPunchActionSource(
    weeklyPunchSource(day, 'in'),
    weeklyPunchSource(day, 'out'),
  );
}

export function weeklyDayToAttendanceValues(
  day: WeeklyAttendanceDay,
): Record<string, unknown> {
  const statusCode =
    day.statusCode === 'WEEKEND'
      ? 'WEEKLY_OFF'
      : day.statusCode === 'ON_TIME' || day.statusCode === 'EARLY_EXIT'
        ? 'PRESENT'
        : day.statusCode;
  return {
    attendanceDate: day.date,
    firstCheckInAt: day.arrivalTime,
    lastCheckOutAt: day.exitTime,
    netWorkMinutes: day.workedMinutes,
    breakMinutes: day.lunchBreakMinutes,
    expectedWorkMinutes: day.expectedWorkMinutes,
    spreadMinutes: day.spreadMinutes,
    policyLunchBreakMinutes: day.policyLunchBreakMinutes,
    grossWorkMinutes: day.totalMinutes,
    lateMinutes: day.lateMinutes,
    shortHoursMinutes: day.shortHoursMinutes,
    isLate: day.isLate ?? day.lateMinutes > 0,
    isShortHours: day.isShortHours,
    isPresent: day.isPresent,
    isHalfDay: day.isHalfDay ?? statusCode === 'HALF_DAY',
    isHalfDayLeave: day.isHalfDayLeave ?? statusCode === 'HALF_DAY_LEAVE',
    isOnLeave: day.isOnLeave ?? statusCode === 'ON_LEAVE',
    isWfh: day.isWfh ?? statusCode === 'WFH',
    isHoliday: statusCode === 'HOLIDAY',
    isWeeklyOff: statusCode === 'WEEKLY_OFF',
    missingCheckOut: day.missingCheckOut ?? statusCode === 'MISSING_CHECKOUT',
    isHoliday: day.isHoliday ?? statusCode === 'HOLIDAY',
    isWeeklyOff: day.isWeeklyOff ?? statusCode === 'WEEKLY_OFF',
    statusCode,
    statusLabel: day.statusLabel,
    dayStatus: day.dayStatus,
    permissionUsed: day.permissionUsed,
    permissionMinutes: day.permissionMinutes,
    permissionTypeName: day.permissionTypeName,
    permissionStartTime: day.permissionStartTime,
    permissionEndTime: day.permissionEndTime,
    leaveTypeName: day.leaveTypeName,
    leaveSessionName: day.leaveSessionName,
    leaveSessionType: day.leaveSessionType,
    leaveDays: day.leaveDays,
    attendanceMode: day.attendanceMode,
    checkInSource: day.checkInSource,
    checkOutSource: day.checkOutSource,
    checkInOriginalSource: day.checkInOriginalSource,
    checkOutOriginalSource: day.checkOutOriginalSource,
    checkInKioskId: day.checkInKioskId,
    checkOutKioskId: day.checkOutKioskId,
    checkInFaceVerified: day.checkInFaceVerified,
    checkOutFaceVerified: day.checkOutFaceVerified,
    checkInDeviceIdentifier: day.checkInDeviceIdentifier,
    checkOutDeviceIdentifier: day.checkOutDeviceIdentifier,
    checkInKioskName: day.checkInKioskName,
    checkOutKioskName: day.checkOutKioskName,
    checkInLocationName: day.checkInLocationName,
    checkOutLocationName: day.checkOutLocationName,
    isManuallyAdjusted: day.isManuallyAdjusted,
  };
}

function statusCodeFromDailyRow(row: AttendanceReportRow, fallback: string): string {
  if (row.status === 'Half-Day Absent') return 'HALF_DAY';
  if (row.status === 'Half-Day Leave' || row.status === 'Half-Day Present + Half-Day Leave') {
    return 'HALF_DAY_LEAVE';
  }
  if (row.status === 'On Leave') return 'ON_LEAVE';
  if (row.status === 'WFH') return 'WFH';
  if (row.status === 'Holiday') return 'HOLIDAY';
  if (row.status === 'Weekly Off') return 'WEEKLY_OFF';
  if (row.status === 'LOP') return 'LOP';
  if (row.status === 'Absent') return 'ABSENT';
  if (row.status === 'Short Hours') return 'SHORT_HOURS';
  if (row.status === 'Late') return 'LATE';
  if (row.status === 'Present') return 'PRESENT';
  if (row.isMissingCheckout) return 'MISSING_CHECKOUT';
  return fallback;
}

/**
 * Reuse Daily Attendance mapping so weekly day labels/values match that screen.
 */
export function applyDailyAttendanceDisplayToWeeklyDay(
  day: WeeklyAttendanceDay,
): WeeklyAttendanceDay {
  const mapped = mapAttendanceReportRows([
    { rowKey: day.date, values: weeklyDayToAttendanceValues(day) },
  ])[0];
  const shortHoursMinutes = !mapped.isShortHours
    ? 0
    : day.dayStatus
      ? Math.max(0, day.expectedWorkMinutes - (day.workedMinutes ?? 0))
      : shortHoursMinutesFromCoverage(
          day.workedMinutes,
          day.expectedWorkMinutes,
        ) || day.shortHoursMinutes;
  return {
    ...day,
    shortHoursMinutes,
    statusCode: day.dayStatus?.code ?? statusCodeFromDailyRow(mapped, day.statusCode),
    statusLabel: mapped.status,
    statusNotes: mapped.statusNotes,
    statusTone: mapped.statusTone,
    dayDetails: mapped.dayDetails,
    locationMode: mapped.locationMode,
    checkIn: mapped.checkIn,
    checkOut: mapped.checkOut,
    workHours: mapped.workHours,
    breakTime: mapped.breakTime,
    expectedHours: mapped.expectedHours,
    totalHours: mapped.totalHours,
    totalHoursCovered: mapped.totalHoursCovered,
    hoursCovered: mapped.hoursCovered,
    attendanceType: mapped.attendanceType,
    officeName: mapped.officeName,
    kioskName: mapped.kioskName,
    checkInKioskName: mapped.checkInKioskName,
    checkOutKioskName: mapped.checkOutKioskName,
    checkInSourceLabel: mapped.checkInSourceLabel,
    checkOutSourceLabel: mapped.checkOutSourceLabel,
    highlightCheckOutSource: mapped.highlightCheckOutSource,
  };
}

export function formatWeeklyStatusLabel(
  day: Pick<WeeklyAttendanceDay, 'statusLabel' | 'statusNotes'>,
): string {
  return formatStatusWithNotes(day.statusLabel, day.statusNotes);
}

export function formatWeeklyPermissionStatus(
  day: Pick<
    WeeklyAttendanceDay,
    | 'dayDetails'
    | 'permissionUsed'
    | 'permissionMinutes'
    | 'permissionTypeName'
    | 'permissionStartTime'
    | 'permissionEndTime'
    | 'leaveTypeName'
    | 'leaveSessionName'
    | 'leaveSessionType'
    | 'leaveDays'
    | 'isHalfDayLeave'
  >,
): string {
  if (isTimePermissionEnabled() && day.dayDetails) return day.dayDetails;
  return formatAttendanceReportDayDetails({
    permissionUsed: day.permissionUsed,
    permissionMinutes: day.permissionMinutes,
    permissionTypeName: day.permissionTypeName,
    permissionStartTime: day.permissionStartTime,
    permissionEndTime: day.permissionEndTime,
    leaveTypeName: day.leaveTypeName,
    leaveSessionName: day.leaveSessionName,
    leaveSessionType: day.leaveSessionType,
    leaveDays: day.leaveDays,
    isHalfDayLeave: day.isHalfDayLeave,
  });
}

/** @deprecated Type column removed; kiosk names render next to check-in/out. */
export function formatWeeklyTypeDetails(
  _day: Pick<
    WeeklyAttendanceDay,
    'attendanceType' | 'officeName' | 'kioskName' | 'locationMode'
  >,
): string {
  return '—';
}

/** @deprecated Display now uses Daily Attendance mapping. */
export function applyWorkHoursCoverageToWeeklyDay(
  day: WeeklyAttendanceDay,
): WeeklyAttendanceDay {
  return applyDailyAttendanceDisplayToWeeklyDay(day);
}

export function matchesEmployeeSearch(
  name: string,
  email: string | null,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    name.toLowerCase().includes(needle) ||
    (email ?? '').toLowerCase().includes(needle)
  );
}
